import HPOCategorization from '@/features/terminology/hpo/HPOCategorization'
import HPOSearch from '@/features/terminology/hpo/HPOSearchClient'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { updateReasonData } from '@/store/slices/formSlice'
import { FormSection } from '@/types/FormSectionInterface'
import { CategorizedHPOOption, HPOOption, ReasonForTestingData } from '@/types/RequisitionFormTypes'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

interface ReasonForTestingProps {
  onDataChange?: (data: ReasonForTestingData) => void
  onComplete?: (isComplete: boolean) => void
}

const ReasonForTesting = forwardRef<FormSection, ReasonForTestingProps>(({ onDataChange, onComplete }, ref) => {
  const dispatch = useAppDispatch()
  const { formData } = useAppSelector((state) => state.form)

  const [showConstrueNotification, setShowConstrueNotification] = useState(false)
  const [categorizedTerms, setCategorizedTerms] = useState<CategorizedHPOOption[]>([])

  // Helper function to convert array data to string for display
  const arrayToString = (data: unknown): string => {
    if (Array.isArray(data)) {
      return data
        .map(
          (item: { id: string; label: string; description?: string }) =>
            `${item.id}: ${item.label}${item.description ? ` - ${item.description}` : ''}`
        )
        .join('\n')
    }
    return String(data || '')
  }

  // Helper function to convert HPOOption[] to string for display
  const hpoOptionsToString = (options: HPOOption[]): string => {
    return options.map((option) => `${option.id}: ${option.label}`).join('\n')
  }

  // Default data - simplified since Redux is the source of truth
  const getDefaultData = useCallback((): ReasonForTestingData => {
    return {
      clinicalDiagnosis: [],
      icd10Diagnosis: '',
      userInteracted: false,
      categorizedTerms: [],
    }
  }, [])

  // Use Redux state as the source of truth
  const reasonData = formData.reason
  const construeResults = formData.clinical?.construeResults
  const isInitialized = useRef(false)

  // Implement the FormSection interface
  useImperativeHandle(ref, () => ({
    isSectionComplete: () => {
      return FormCompletionChecker.isReasonComplete(reasonData)
    },
    renderSection: () => {
      // Return the main component render to avoid duplication
      return <ReasonForTesting />
    },
    renderSectionInfo: () => {
      return <div></div>
    },
  }))

  // Initialize form and handle construe results
  useEffect(() => {
    if (isInitialized.current) return

    // If we have existing data in Redux, use it
    if (reasonData && (reasonData.clinicalDiagnosis.length > 0 || reasonData.userInteracted)) {
      setCategorizedTerms(reasonData.clinicalDiagnosis)
      onDataChange?.(reasonData)
      const isComplete = FormCompletionChecker.isReasonComplete(reasonData)
      onComplete?.(isComplete)
      isInitialized.current = true
      return
    }

    // If we have construe results but no existing data, process them
    if (construeResults?.terms && construeResults.terms.length > 0) {
      // Use construe results to populate HPO terms as HPOOption[]
      const construeHPOOptions: HPOOption[] = construeResults.terms.map((term) => ({
        id: term.id,
        label: term.label,
        node: { id: term.id, lbl: term.label, type: 'phenotype' },
      }))

      // Initialize categorized terms with construe results (default to 'Key' category)
      const initialCategorizedTerms: CategorizedHPOOption[] = construeHPOOptions.map((option) => ({
        ...option,
        category: 'Key' as const,
      }))

      const updatedData = {
        clinicalDiagnosis: initialCategorizedTerms,
        icd10Diagnosis: '',
        userInteracted: true,
      }

      // Update Redux state
      dispatch(updateReasonData(updatedData))
      setCategorizedTerms(initialCategorizedTerms)
      onDataChange?.({ ...updatedData, categorizedTerms: initialCategorizedTerms })

      // Show notification that construe results have been loaded
      setShowConstrueNotification(true)
      setTimeout(() => setShowConstrueNotification(false), 3000) // Hide after 3 seconds

      // Mark as complete since we have construe results
      onComplete?.(true)
    } else {
      // No existing data and no construe results - use default empty state
      const defaultData = getDefaultData()
      setCategorizedTerms(defaultData.clinicalDiagnosis)
      onDataChange?.(defaultData)
      const isComplete = FormCompletionChecker.isReasonComplete(defaultData)
      onComplete?.(isComplete)
    }

    isInitialized.current = true
  }, [reasonData, construeResults, onDataChange, onComplete, getDefaultData, dispatch]) // Include all dependencies

  // Handle completion status changes when user interacts with form
  useEffect(() => {
    const isComplete = FormCompletionChecker.isReasonComplete(reasonData)
    onComplete?.(isComplete)
  }, [reasonData, onComplete])

  const handleHPOChange = (selectedOptions: HPOOption[]) => {
    // Update categorized terms - add new terms with default 'Key' category
    const existingTermIds = new Set(categorizedTerms.map((term) => term.id))
    const newTerms = selectedOptions
      .filter((option) => !existingTermIds.has(option.id))
      .map((option) => ({
        ...option,
        category: 'Key' as const,
      }))

    // Keep existing categorized terms and add new ones
    const updatedCategorizedTerms = [
      ...categorizedTerms.filter((term) => selectedOptions.some((option) => option.id === term.id)),
      ...newTerms,
    ]

    setCategorizedTerms(updatedCategorizedTerms)

    // Update the main data with categorized terms as clinicalDiagnosis
    const newData = { ...reasonData, clinicalDiagnosis: updatedCategorizedTerms, userInteracted: true }

    // Update Redux state
    dispatch(updateReasonData(newData))

    // Pass data with categorized terms
    const dataWithCategories = { ...newData, categorizedTerms: updatedCategorizedTerms }
    onDataChange?.(dataWithCategories)
  }

  const handleCategoryChange = (termId: string, newCategory: 'Key' | 'Additional' | 'Excluded') => {
    const updatedCategorizedTerms = categorizedTerms.map((term) =>
      term.id === termId ? { ...term, category: newCategory } : term
    )
    setCategorizedTerms(updatedCategorizedTerms)

    // Update the main data with updated categorized terms as clinicalDiagnosis
    const newData = { ...reasonData, clinicalDiagnosis: updatedCategorizedTerms }

    // Update Redux state
    dispatch(updateReasonData(newData))

    // Pass updated categorized terms to parent
    const dataWithCategories = { ...newData, categorizedTerms: updatedCategorizedTerms }
    onDataChange?.(dataWithCategories)
  }

  const handleICD10Change = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target
    const newData = { ...reasonData, icd10Diagnosis: value, userInteracted: true }

    // Update Redux state
    dispatch(updateReasonData(newData))

    // Also call the callback for backward compatibility
    onDataChange?.(newData)
  }

  // Get display values for textareas
  const getDisplayValue = (field: keyof ReasonForTestingData): string => {
    const value = reasonData[field]
    if (Array.isArray(value)) {
      // Check if it's HPOOption[] or CodeableConcept[]
      if (value.length > 0 && 'node' in value[0]) {
        return hpoOptionsToString(value as HPOOption[])
      }
      return arrayToString(value)
    }
    return String(value || '')
  }

  // Get HPO options for the search component
  const getHPOOptions = (): HPOOption[] => {
    const value = reasonData.clinicalDiagnosis
    if (Array.isArray(value) && value.length > 0 && 'node' in value[0]) {
      return value as HPOOption[]
    }
    return []
  }

  return (
    <div className="space-y-4">
      {/* Construe Results Loaded Notification */}
      {showConstrueNotification && (
        <div className="animate-pulse rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-center">
            <svg className="mr-3 h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-green-900">Analysis Results Loaded!</h4>
              <p className="text-sm text-green-700">
                HPO terms have been automatically populated from your clinical note analysis.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Construe Status Indicator */}
      {construeResults && getDisplayValue('clinicalDiagnosis').trim() !== '' && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center">
            <svg className="mr-3 h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-blue-900">Clinical Note Analysis Complete</h4>
              <p className="text-sm text-blue-700">
                HPO terms have been automatically identified from your clinical note.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-10">
        <label className="mb-1 block text-sm font-medium text-gray-700">HPO Terms</label>
        <HPOSearch
          id="clinicalDiagnosis"
          value={getHPOOptions()}
          onChange={handleHPOChange}
          placeholder="Search for HPO terms..."
          required={false}
          maxSelections={10}
        />

        {/* HPO Categorization Component */}
        <HPOCategorization
          categorizedTerms={categorizedTerms}
          onCategoryChange={handleCategoryChange}
          title="Categorize HPO Terms"
        />
      </div>

      <div>
        <div>
          <label htmlFor="icd10Diagnosis" className="form-label mb-1 block">
            ICD-10 Codes
          </label>
          <textarea
            id="icd10Diagnosis"
            name="icd10Diagnosis"
            value={getDisplayValue('icd10Diagnosis')}
            onChange={handleICD10Change}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Enter ICD-10 diagnosis codes..."
            rows={4}
          />
        </div>
      </div>
    </div>
  )
})

export default ReasonForTesting
